import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { games } from '../data/games';
import { parseScoreType, secondsToTimeString } from '../utils/scoreUtils';

const formatTimeScore = (seconds) => secondsToTimeString(seconds).replace(/^0(?=\d:)/, '');

function ScoresTable({ friendsData, showModal }) {
  const navigate = useNavigate();
  const [currentGamePage, setCurrentGamePage] = useState(0);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Handle window resize to recalculate columns
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setCurrentGamePage(0); // Reset to first page when screen size changes
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get all games that have scores recorded today (by user or friends)
  const getGamesWithScores = () => {
    if (!friendsData) return [];
    
    const gameIdsWithScores = new Set();
    
    // Add games from user's plays
    Object.keys(friendsData.user?.today_plays || {}).forEach(gameId => {
      gameIdsWithScores.add(gameId);
    });
    
    // Add games from friends' plays
    (friendsData.friends || []).forEach(friend => {
      Object.keys(friend.today_plays || {}).forEach(gameId => {
        gameIdsWithScores.add(gameId);
      });
    });
    
    // Convert game IDs to game objects and sort with user's games first
    return Array.from(gameIdsWithScores)
      .map(gameId => games.find(game => parseInt(game.id) === parseInt(gameId)))
      .filter(Boolean)
      .sort((a, b) => {
        // Check if user has scores for each game
        const userHasScoreA = !!(friendsData.user?.today_plays && friendsData.user.today_plays[parseInt(a.id)]);
        const userHasScoreB = !!(friendsData.user?.today_plays && friendsData.user.today_plays[parseInt(b.id)]);
        
        // If one has user score and other doesn't, prioritize the one with user score
        if (userHasScoreA && !userHasScoreB) return -1;
        if (!userHasScoreA && userHasScoreB) return 1;
        
        // If both have user scores or both don't, sort alphabetically
        return a.name.localeCompare(b.name);
      });
  };

  const gamesWithScores = getGamesWithScores();

  // Calculate how many game columns can fit based on screen width
  // Reserve space for padding/margins: ~40px
  const tablePadding = 40;
  const gameColumnWidth = 64; // Fixed width for game columns
  const availableWidth = screenWidth - tablePadding;
  const maxGameColumns = Math.max(1, Math.floor((availableWidth - 120) / gameColumnWidth)); // Reserve min 120px for username
  
  // Dynamic pagination based on screen width
  const gamesPerPage = Math.min(maxGameColumns, gamesWithScores.length);
  const totalPages = Math.ceil(gamesWithScores.length / gamesPerPage);
  const startIndex = currentGamePage * gamesPerPage;
  const endIndex = startIndex + gamesPerPage;
  const currentPageGames = gamesWithScores.slice(startIndex, endIndex);
  
  // Calculate flexible username column width (fills remaining space)
  const gameColumnsWidth = currentPageGames.length * gameColumnWidth;
  const usernameColumnWidth = availableWidth - gameColumnsWidth;

  const goToNextPage = () => {
    if (currentGamePage < totalPages - 1) {
      setCurrentGamePage(currentGamePage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentGamePage > 0) {
      setCurrentGamePage(currentGamePage - 1);
    }
  };

  // Helper function to get score for a specific user and game today
  const getScoreForUserGame = (userId, gameId, isCurrentUser = false) => {
    if (!friendsData) return null;
    
    if (isCurrentUser) {
      // For current user, use today_plays from user object
      const play = friendsData.user?.today_plays?.[parseInt(gameId).toString()];
      return play ? { score: play.score, message: play.message } : null;
    } else {
      // For friends, find the friend and get their today_plays
      const friend = friendsData.friends?.find(f => f.id === userId);
      if (friend) {
        const play = friend.today_plays?.[parseInt(gameId).toString()];
        return play ? { score: play.score, message: play.message } : null;
      }
      return null;
    }
  };

  // Helper function to handle score cell click
  const handleScoreCellClick = (gameId, isCurrentUser, userId, username) => {
    if (isCurrentUser) {
      // Navigate to edit score form
      navigate(`/edit-score/${gameId}`, {
        state: { from: '/friends' }
      });
    } else {
      // Show modal with friend's message if it exists
      const scoreData = getScoreForUserGame(userId, gameId, false);
      if (scoreData && scoreData.message && scoreData.message.trim()) {
        showModal(scoreData.message, `${username}'s Message`);
      }
    }
  };

  // Create table data: user first, then friends
  const tableData = [
    {
      username: friendsData?.user?.username || '',
      isCurrentUser: true,
      id: friendsData?.user?.id || null
    },
    ...(friendsData?.friends || []).map(friend => ({
      username: friend.username,
      isCurrentUser: false,
      id: friend.id
    }))
  ];

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-0" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
          <thead>
            <tr className="border-b border-nlg-rule">
              <th className="text-left px-3" style={{ width: `${Math.max(usernameColumnWidth, 120)}px`, minWidth: '120px', maxWidth: `${usernameColumnWidth}px` }}>
                {gamesWithScores.length > gamesPerPage ? (
                  <div className="flex items-center justify-start space-x-1">
                    <button
                      onClick={goToPrevPage}
                      disabled={currentGamePage === 0}
                      className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors duration-150 ${
                        currentGamePage === 0
                          ? 'bg-white/5 text-nlg-muted cursor-not-allowed'
                          : 'bg-nlg-gold text-nlg-bg'
                      }`}
                      title="Previous page"
                    >
                      <svg className="w-3 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18 4l-14 8 14 8z"/>
                      </svg>
                    </button>
                    <div className="flex items-center space-x-1 min-w-[30px]">
                      {Array.from({ length: totalPages }, (_, index) => (
                        <div
                          key={index}
                          className={`w-2.5 h-2.5 rounded-full ${
                            index === currentGamePage
                              ? 'bg-nlg-gold'
                              : 'bg-white/10'
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={goToNextPage}
                      disabled={currentGamePage >= totalPages - 1}
                      className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors duration-150 ${
                        currentGamePage >= totalPages - 1
                          ? 'bg-white/5 text-nlg-muted cursor-not-allowed'
                          : 'bg-nlg-gold text-nlg-bg'
                      }`}
                      title="Next page"
                    >
                      <svg className="w-3 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4l14 8-14 8z"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <></>
                )}
              </th>

              <>
                {currentPageGames.map(game => (
                  <th key={game.id} className="text-center py-2" style={{ width: `${gameColumnWidth}px`, minWidth: '48px', maxWidth: `${gameColumnWidth}px` }}>
                    <img
                      src={`/img/games/${game.image}`}
                      alt={game.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-lg border border-white/10"
                      title={game.name}
                    />
                  </th>
                ))}
              </>

            </tr>
          </thead>
          <tbody>
            {tableData.map(player => (
              <tr
                key={player.username}
                className="border-b border-white/5 last:border-none transition-colors duration-150 hover:bg-white/5"
              >
                <td className="py-2 px-3 font-display font-bold text-sm sm:text-base text-nlg-text text-left truncate" style={{ width: `${Math.max(usernameColumnWidth, 120)}px`, minWidth: '120px', maxWidth: `${usernameColumnWidth}px` }}>
                  {player.username}
                </td>
                <>
                    {currentPageGames.map((game, index) => {
                      const scoreData = getScoreForUserGame(player.id, parseInt(game.id), player.isCurrentUser);
                      const hasScore = scoreData && scoreData.score;
                      const hasMessage = scoreData && scoreData.message;

                      // Check if current user has a score for this game
                      const currentUserScoreData = getScoreForUserGame(friendsData?.user?.id, parseInt(game.id), true);
                      const currentUserHasScore = currentUserScoreData && currentUserScoreData.score;

                      // Format score based on game's scoreType
                      const scoreTypeInfo = game.scoreType ? parseScoreType(game.scoreType) : null;
                      const displayScore = hasScore && scoreTypeInfo?.type === 'time'
                        ? formatTimeScore(scoreData.score)
                        : scoreData?.score;

                      return (
                        <td
                          key={game.id}
                          className={`text-center py-1 px-1 ${player.isCurrentUser || (hasScore && hasMessage) ? 'cursor-pointer' : ''}`}
                          style={{ width: `${gameColumnWidth}px`, minWidth: '48px', maxWidth: `${gameColumnWidth}px` }}
                          onClick={() => handleScoreCellClick(parseInt(game.id), player.isCurrentUser, player.id, player.username)}
                        >
                          {hasScore ? (
                            <div className="relative inline-block">
                              <span className="text-nlg-cream font-medium text-xl">
                                {displayScore}
                              </span>
                              {hasMessage && (player.isCurrentUser || (!player.isCurrentUser && currentUserHasScore)) && (
                                <div className="absolute -top-1 -right-3 w-3 h-3 bg-nlg-gold rounded-full flex items-center justify-center">
                                  <span className="text-nlg-bg text-sm">💬</span>
                                </div>
                              )}
                            </div>
                          ) : player.isCurrentUser ? (
                            <div className="text-nlg-muted transition-colors">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mx-auto">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </div>
                          ) : (
                            <span className="text-nlg-muted"> - </span>
                          )}
                        </td>
                      );
                    })}
                  </>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ScoresTable;